import styled, { keyframes } from 'styled-components';
import { Button, Input, Table, Tag, Modal } from 'antd';
import { COLORS } from '../../../constants/colors';

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const slideInFromTop = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const PageContainer = styled.div`
  min-height: 100vh;
  
  .header-animate-in {
    animation: ${slideInFromTop} 0.6s ease-out forwards;
    opacity: 0;
  }

  .table-animate-in {
    animation: ${fadeInScale} 0.6s ease-out forwards;
    animation-delay: 0.2s;
    opacity: 0;
  }
`;

export const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;
`;

export const SearchInput = styled(Input)`
  max-width: 320px;
  height: 40px !important;
  min-height: 40px !important;
  border-radius: 8px !important;
  border: 1px solid ${COLORS.border} !important;
  font-size: 14px !important;
  box-shadow: none !important;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${COLORS.$primaryBlue} !important;
  }

  &:focus,
  &.ant-input-affix-wrapper-focused {
    border-color: ${COLORS.$primaryBlue} !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
  }

  .ant-input {
    font-size: 14px;
  }

  .ant-input-prefix {
    margin-right: 8px;

    .anticon {
      color: ${COLORS.textMuted};
      font-size: 16px;
    }
  }
`;

export const FilterSelect = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

export const ActionButton = styled(Button)`
  border-radius: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const StyledTable = styled(Table)`
  background: white;
  border-radius: 16px;
  border: 1px solid #f0f0f0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .ant-table {
    border-radius: 0;
    overflow: hidden;
    background: white;
    border: none;
  }

  .ant-table-thead > tr > th {
    background: white !important;
    color: ${COLORS.textDark} !important;
    border-bottom: 3px solid ${COLORS.$charcoalBlack} !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    height: 52px !important;
    padding: 14px 20px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    white-space: nowrap !important;
    position: relative;

    &:first-child {
      border-top-left-radius: 16px;
    }

    &:last-child {
      border-top-right-radius: 16px;
    }
  }

  .ant-table-tbody > tr {
    transition: all 0.2s ease;
    border-bottom: 1px solid #f5f5f5;

    &:hover > td {
      background: #fafbff !important;
      transform: translateY(-1px);
    }

    &:last-child {
      border-bottom: none;

      td {
        border-bottom: none;
      }
    }

    &:nth-child(even) {
      background: #fafafa;

      &:hover > td {
        background: #f0f7ff !important;
      }
    }
  }

  .ant-table-tbody > tr > td {
    border-bottom: none;
    padding: 18px 20px;
    font-size: 14px;
    line-height: 1.5;
    vertical-align: middle;
    text-align: left;
    transition: all 0.2s ease;
  }

  /* Hide scrollbar on header */
  .ant-table-header {
    overflow: hidden !important;

    &::-webkit-scrollbar {
      display: none;
      height: 0;
    }

    scrollbar-width: none;
  }

  /* Scrollbar styling - body rows only */
  .ant-table-body {
    overflow: auto !important;

    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f5f5f5;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 4px;
      border: 1px solid #f5f5f5;

      &:hover {
        background: #a8a8a8;
      }
    }

    &::-webkit-scrollbar-corner {
      background: #f5f5f5;
    }

    scrollbar-width: thin;
    scrollbar-color: #c1c1c1 #f5f5f5;
  }

  /* Fixed column styling */
  .ant-table-cell-fix-right {
    background: white !important;
    box-shadow: -6px 0 16px -8px rgba(0, 0, 0, 0.08);
  }

  .ant-table-thead > tr > th.ant-table-cell-fix-right {
    background: white !important;
  }

  .ant-table-tbody > tr:nth-child(even) > td.ant-table-cell-fix-right {
    background: #fafafa !important;
  }

  .ant-table-tbody > tr:hover > td.ant-table-cell-fix-right {
    background: #fafbff !important;
  }

  .ant-table-tbody > tr:nth-child(even):hover > td.ant-table-cell-fix-right {
    background: #f0f7ff !important;
  }

  /* Pagination styling */
  .ant-pagination {
    margin: 0 !important;
    padding: 12px 16px !important;
    border-top: 1px solid ${COLORS.border};
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;

    .ant-pagination-total-text {
      font-size: 13px;
      color: ${COLORS.textMedium};
      font-weight: 500;
      flex: 1;
    }

    .ant-pagination-options {
      display: flex;
      align-items: center;
      gap: 8px;
      height: 32px;

      .ant-pagination-options-size-changer {
        height: 32px !important;

        .ant-select-selector {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          min-height: 32px !important;
          min-width: 100px !important;
          transition: all 0.3s ease !important;
          display: flex !important;
          align-items: center !important;
          padding: 0 28px 0 12px !important;
          box-sizing: border-box !important;

          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
        }

        .ant-select-selection-item {
          height: 28px !important;
          line-height: 28px !important;
          padding: 0 !important;
          display: flex !important;
          align-items: center !important;
          font-weight: 500 !important;
          font-size: 13px !important;
          flex: 1 !important;
          min-width: 0 !important;
        }

        .ant-select-selection-search {
          height: 28px !important;
          padding-left: 8px !important;

          .ant-select-selection-search-input {
            height: 28px !important;
            line-height: 28px !important;
          }
        }

        .ant-select-arrow {
          right: 10px !important;
          flex-shrink: 0 !important;
          height: 14px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;

          .anticon {
            line-height: 1 !important;
            vertical-align: middle !important;
          }
        }

        &.ant-select-focused .ant-select-selector {
          border-color: ${COLORS.$primaryBlue} !important;
          box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
        }
      }

      .ant-pagination-options-quick-jumper {
        display: flex;
        align-items: center;
        height: 32px;
        font-size: 13px;

        .ant-input {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          min-height: 32px !important;
          width: 50px !important;
          text-align: center;
          font-weight: 600;
          font-size: 13px;
          transition: all 0.3s ease !important;
          line-height: 28px !important;
          padding: 0 0 0 8px !important;

          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }

          &:focus {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
          }
        }
      }
    }

    .ant-pagination-item {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;

      a {
        color: ${COLORS.textDark};
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        text-decoration: none;
        font-size: 13px;
      }

      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);

        a {
          color: ${COLORS.$primaryBlue};
        }
      }

      &.ant-pagination-item-active {
        background: ${COLORS.$primaryBlue};
        border-color: ${COLORS.$primaryBlue};
        transform: translateY(-1px);

        a {
          color: white;
          font-weight: 700;
        }

        &:hover {
          background: ${COLORS.$primaryBlueDark};
          border-color: ${COLORS.$primaryBlueDark};

          a {
            color: white;
          }
        }
      }
    }

    .ant-pagination-prev,
    .ant-pagination-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;

      .anticon {
        color: ${COLORS.textDark};
        font-size: 14px;
      }

      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);

        .anticon {
          color: ${COLORS.$primaryBlue};
        }
      }

      &.ant-pagination-disabled {
        border-color: #e8e8e8;
        background: #f5f5f5;

        .anticon {
          color: ${COLORS.textMuted};
        }

        &:hover {
          border-color: #e8e8e8;
          background: #f5f5f5;
          transform: none;

          .anticon {
            color: ${COLORS.textMuted};
          }
        }
      }
    }

    .ant-pagination-jump-prev,
    .ant-pagination-jump-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;

      .anticon {
        color: ${COLORS.textMuted};
        font-size: 14px;
      }

      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);

        .anticon {
          color: ${COLORS.$primaryBlue};
        }
      }
    }
  }
`;

export const StatusTag = styled(Tag)`
  border-radius: 6px;
  font-weight: 500;
  font-size: 12px;
  padding: 2px 10px;
  border: none;
`;

export const InfoTag = styled(Tag)`
  border-radius: 4px;
  font-size: 12px;
  margin: 2px;
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  color: #555;
`;

export const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
  }

  .ant-modal-header {
    padding: 20px 24px;
    border-bottom: 1px solid #f0f0f0;
  }

  .ant-modal-title {
    font-size: 18px;
    font-weight: 600;
  }

  .ant-modal-body {
    padding: 24px;
    max-height: 70vh;
    overflow-y: auto;
  }

  .ant-modal-footer {
    padding: 16px 24px;
    border-top: 1px solid #f0f0f0;
  }
`;

export const FormSection = styled.div`
  margin-bottom: 20px;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: ${COLORS.$charcoalBlack};
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  
  .icon {
    font-size: 48px;
    color: #d9d9d9;
    margin-bottom: 16px;
  }
  
  .title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }
  
  .description {
    font-size: 14px;
    color: #888;
    margin-bottom: 24px;
  }

  button {
    display: inline-flex;
    color: #1890ff !important;
    border-color: #1890ff !important;

    &:hover {
      color: #40a9ff !important;
      border-color: #40a9ff !important;
    }
  }
`;

export const StatsRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

export const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px 24px;
  flex: 1;
  min-width: 150px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 16px;

  .stat-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    background: ${props => props.$bgColor || '#f0f0f0'};
    color: ${props => props.$iconColor || '#333'};
  }

  .stat-info {
    .stat-value {
      font-size: 24px;
      font-weight: 700;
      color: ${COLORS.$charcoalBlack};
      line-height: 1;
    }

    .stat-label {
      font-size: 13px;
      color: #888;
      margin-top: 4px;
    }
  }
`;

export const ApprovalBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch (props.$status) {
      case 'approved': return '#f6ffed';
      case 'pending': return '#fff7e6';
      case 'rejected': return '#fff2f0';
      case 'suspended': return '#f5f5f5';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'approved': return '#52c41a';
      case 'pending': return '#faad14';
      case 'rejected': return '#ff4d4f';
      case 'suspended': return '#888';
      default: return '#888';
    }
  }};
`;
